<template>
  <TransitionRoot
    appear
    :show="isOpen"
    as="template">
    <Dialog
      as="div"
      class="fixed inset-0 z-50 flex max-h-screen items-center justify-center px-4 py-4 lg:py-8"
      data-lenis-prevent
      @close="setIsOpen">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0">
        <div
          class="absolute inset-0 bg-black/30"
          aria-hidden="true"
          @click="setIsOpen(false)" />
      </TransitionChild>

      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0 scale-95"
        enter-to="opacity-100 scale-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100 scale-100"
        leave-to="opacity-0 scale-95">
        <DialogPanel
          data-lenis-prevent
          class="relative z-10 flex max-h-full w-full max-w-[1140px] flex-col overflow-auto rounded-3xl bg-white p-4 max-lg:min-h-full lg:p-6">
          <template v-if="state !== 'success'">
            <p class="pr-10 text-lg text-gray-300">
              {{ tagline }}
            </p>
            <DialogTitle class="pr-10 text-2xl">
              {{ title }}
            </DialogTitle>

            <JobModalSteps
              :steps="{ step1: step1.info.title, step2: step2.title }"
              :current="step"
              @change-step="changeStep" />
            <div class="flex-grow">
              <JobModalStep1
                v-if="step === 0"
                v-bind="step1"
                :is-campaign-position="isCampaignPosition"
                :inputs="inputs"
                :errors="errors"
                @input-change="setInput" />
              <JobModalStep2
                v-if="step === 1"
                v-bind="step2"
                :is-campaign-position="isCampaignPosition"
                :values="inputs"
                @input-change="setInput" />
            </div>

            <JobModalButtons
              v-bind="{ step2, step1, step }"
              :error-msg="error"
              :state="state"
              @change-step="changeStep"
              @handle-button-click="handleButtonClick" />
          </template>

          <JobModalSuccess
            v-else
            :title="success.title"
            :subtitle="success.subtitle" />

          <Presence>
            <Motion
              v-if="state === 'loading'"
              :initial="{ opacity: 0 }"
              :animate="{ opacity: [0, 1] }"
              :exit="{ opacity: 0 }"
              as="div"
              class="fixed inset-0 z-30 h-full cursor-wait bg-black bg-opacity-20 lg:absolute" />
          </Presence>

          <IconCloseLine
            class="absolute right-4 top-4 h-10 w-10 cursor-pointer lg:right-6 lg:top-6"
            @click="setIsOpen(false)" />
        </DialogPanel>
      </TransitionChild>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
  // @ts-expect-error: Motion One does not provide types
  import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
  import { Motion, Presence } from '@motionone/vue/dist/motion-vue.ssr.js'
  import axios from 'axios'

  import campaignPositions from '~/data/campaign-job-positions.json'
  import type { FormStateType } from '~/models/form'

  type CampaignPositionsKey = keyof typeof campaignPositions

  type Props = {
    id: string
    isOpen: boolean
    title: string
    tagline: string
    step1: {
      title: string
      info: {
        title: string
        description: string
        name: { label: string; placeholder: string; error: string }
        lastName: { label: string; placeholder: string; error: string }
        email: { label: string; placeholder: string; error: string }
      }
      attachments: {
        title: string
        description: string
        cv: { label: string; placeholder: string; error: string }
        cover: { label: string; placeholder: string; error: string }
        links: { label: string; placeholder: string }
        notice: string
      }
      mandatory: string
      cta: string
    }
    step2: {
      title: string
      info: {
        title: string
        contact: {
          title: string
          name: string
          lastName: string
          email: string
        }
        attachments: {
          title: string
          cv: string
          cover: string
          links: string
        }
      }
      additional: {
        title: string
        description: string
        message: {
          label: string
          placeholder: string
        }
      }
      cta: string
      back: string
    }
    success: {
      title: string
      subtitle: string
    }
    error: string
  }

  type GhObject = {
    [propName: string]: string
  }

  type GreenhouseQuestion = {
    label: string
    fields: {
      name: string
    }[]
  }

  const props = defineProps<Props>()
  const emit = defineEmits(['toggle'])

  const state = ref<FormStateType>('idle')

  const route = useRoute()
  const { $event } = useNuxtApp()

  const ghFields: GhObject = reactive({})
  const step = ref(0)
  const inputs = ref({
    referrer: '',
    name: '',
    lastName: '',
    email: '',
    linkedin: '',
    cv: null,
    cover: null,
    links: '',
    message: ''
  })

  const errors = ref(new Set<string>())

  const isCampaignPosition = computed(() => {
    return (Object.keys(campaignPositions) as CampaignPositionsKey[]).some(key => {
      return route.path.includes(campaignPositions[key].slug) && campaignPositions[key].utm === route.query.utm_campaign
    })
  })

  onMounted(() => {
    axios({
      method: 'get',
      url: `https://boards-api.greenhouse.io/v1/boards/outfit7/jobs/${props.id}?questions=true`
    })
      .then(response => {
        const questions = response.data.questions as GreenhouseQuestion[]

        questions.forEach(question => {
          ghFields[question.label] = question.fields[0].name
        })
      })
      .catch(console.error)
  })

  function setIsOpen(isOpen: boolean) {
    setTimeout(() => {
      state.value = 'idle'
    }, 500)

    emit('toggle', isOpen)
  }

  function setInput(
    value: string | FileList,
    name: 'referrer' | 'name' | 'lastName' | 'email' | 'linkedin' | 'cv' | 'cover' | 'links' | 'message'
  ) {
    inputs.value[name] = value

    if (errors.value.size > 0 || value === '') {
      validateInputs()
    }
  }

  function handleButtonClick() {
    if (step.value === 0) {
      changeStep(1)
    } else if (step.value === 1) {
      submitForm()
    }
  }

  async function submitForm() {
    state.value = 'loading'

    const data = new FormData()

    data.append('id', props.id)
    data.append('first_name', inputs.value.name)
    data.append('last_name', `${inputs.value.lastName} referral`)
    data.append('email', 'jobs@outfit7.com')

    if (!isCampaignPosition.value) {
      data.append('resume', inputs.value.cv[0])
    }

    if (inputs.value.cover && inputs.value.cover.length > 0) {
      data.append('cover_letter', inputs.value.cover[0])
    }

    if (ghFields['LinkedIn Profile URL']) {
      data.append(ghFields['LinkedIn Profile URL'], inputs.value.linkedin)
    }

    if (ghFields['Website / Portfolio URL']) {
      data.append(ghFields['Website / Portfolio URL'], inputs.value.links)
    }

    if (ghFields['Additional information']) {
      data.append(
        ghFields['Additional information'],
        `Referrer:\n${inputs.value.referrer}\n\nCandidate's email address:\n${inputs.value.email}\n\nRecommendation letter:\n${inputs.value.message}`
      )
    }

    axios({
      method: 'post',
      url: 'https://outfit7.com/submit-application',
      data,
      headers: { 'Content-Type': 'multipart/form-data' }
    })
      .then(() => {
        setTimeout(() => {
          state.value = 'success'

          inputs.value.cover = null
          inputs.value.cv = null
          inputs.value.email = ''
          inputs.value.lastName = ''
          inputs.value.linkedin = ''
          inputs.value.links = ''
          inputs.value.message = ''
          inputs.value.name = ''
          inputs.value.referrer = ''
          step.value = 0
        }, 500)

        $event('Job Application', {
          props: {
            'Submit success (internal)': props.title
          }
        })
      })
      .catch(error => {
        console.error(error)
        state.value = 'error'

        $event('Job Application', {
          props: {
            'Submit failure (internal)': props.title
          }
        })
      })
  }

  function changeStep(newstep: number) {
    validateInputs()
    if (newstep < step.value) {
      errors.value.clear()
    }
    if (errors.value.size === 0) {
      step.value = newstep
      $event('Job Application', { props: { 'Step 2': props.title } })
    }
  }

  function validateInputs() {
    const validEmailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/

    if (inputs.value.referrer?.length) {
      errors.value.delete('referrer')
    } else {
      errors.value.add('referrer')
    }

    if (inputs.value.name && inputs.value.name !== '') {
      errors.value.delete('name')
    } else {
      errors.value.add('name')
    }

    if (inputs.value.lastName && inputs.value.lastName !== '') {
      errors.value.delete('lastName')
    } else {
      errors.value.add('lastName')
    }

    if (inputs.value.email && inputs.value.email.match(validEmailRegex)) {
      errors.value.delete('email')
    } else {
      errors.value.add('email')
    }

    if (isCampaignPosition.value && inputs.value.linkedin && validateHttpURL(inputs.value.linkedin)) {
      errors.value.delete('linkedin')
    } else if (isCampaignPosition.value) {
      errors.value.add('linkedin')
    }

    if (
      !isCampaignPosition.value &&
      inputs.value.cv &&
      inputs.value.cv[0].size / 1024 / 1024 < 10 &&
      validateFileType(inputs.value.cv[0].name)
    ) {
      errors.value.delete('cv')
    } else if (isCampaignPosition.value) {
      errors.value.delete('cv')
    } else {
      errors.value.add('cv')
    }

    if (
      inputs.value.cover &&
      (inputs.value.cover[0].size / 1024 / 1024 > 10 || !validateFileType(inputs.value.cover[0].name))
    ) {
      errors.value.add('cover')
    } else {
      errors.value.delete('cover')
    }
  }

  function validateFileType(fileType: string) {
    const re = /(\.doc|\.pdf|\.docx|\.txt|\.rtf)$/i

    if (!re.exec(fileType)) {
      return false
    }

    return true
  }

  function validateHttpURL(url: string) {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }
</script>
